/**
 * The mock-up's two vertical video blocks (home "Think Tank", about "The Group").
 *
 * The <video> element ships without autoplay: it starts muted on an explicit
 * user action and exposes its own play/mute buttons whose labels change with
 * state, so the control is announced correctly and nothing moves on load
 * (prefers-reduced-motion users get a still poster until they ask for playback).
 */
import { el } from "../core/dom.js";
import type { AppContext } from "../core/context.js";

export interface VideoBlockProps {
	/** Basename in /static, without extension. */
	source: string;
	poster: string;
	/** Distinct DOM id — two video blocks can exist on one page. */
	id: string;
	label: string;
}

export interface VideoBlock {
	/** The framed video with its overlay play affordance. */
	frame: HTMLElement;
	/** Play/pause + mute/unmute buttons, to place inside the surrounding copy. */
	controls: HTMLElement;
}

export function videoBlock(ctx: AppContext, props: VideoBlockProps): VideoBlock {
	const copy = ctx.copy();
	let playing = false;

	const video = el("video", {
		id: props.id,
		class: "mb-video__el",
		src: `/static/${props.source}.mp4`,
		poster: `/static/${props.poster}.webp`,
		muted: true,
		loop: true,
		playsinline: true,
		preload: "metadata",
		"aria-label": props.label,
	});

	const playButton = el(
		"button",
		{ class: "mb-cta mb-cta--accent", type: "button", onClick: () => togglePlay() },
		copy.home.videoPlay,
	);
	const muteButton = el(
		"button",
		{ class: "mb-cta mb-cta--outline", type: "button", onClick: () => toggleMute() },
		copy.home.videoMuteOn,
	);

	const overlay = el(
		"button",
		{
			class: "mb-video__overlay",
			type: "button",
			"aria-label": copy.home.videoPlay,
			onClick: () => togglePlay(),
		},
		el("span", { class: "mb-video__playglyph", "aria-hidden": "true" }, "▶"),
	);

	function syncPlayLabels(): void {
		playButton.textContent = playing ? copy.home.videoPause : copy.home.videoPlay;
		overlay.hidden = playing;
	}

	function togglePlay(): void {
		if (video.paused) {
			void video.play().then(
				() => {
					playing = true;
					syncPlayLabels();
					ctx.track({ type: "video_play", video: props.source });
				},
				() => {
					// Autoplay policies can still refuse; keep the poster and the label honest.
					playing = false;
					syncPlayLabels();
				},
			);
		} else {
			video.pause();
			playing = false;
			syncPlayLabels();
		}
	}

	function toggleMute(): void {
		video.muted = !video.muted;
		muteButton.textContent = video.muted ? copy.home.videoMuteOn : copy.home.videoMuteOff;
	}

	video.addEventListener("pause", () => {
		playing = false;
		syncPlayLabels();
	});
	video.addEventListener("play", () => {
		playing = true;
		syncPlayLabels();
	});

	return {
		frame: el("div", { class: "mb-video" }, video, overlay),
		controls: el("div", { class: "mb-video__controls" }, playButton, muteButton),
	};
}
